import {
  CurrencyDto,
  CustomerTransactionType,
  CustomerTransactionV2Dto,
  TradingType,
  WalletAccountV2Dto,
  WalletCurrencyV2Dto,
  WalletType,
} from 'common/api/autogenerated/client-area';

import { FIAT_MAX_FRACTION } from 'common/constants/fractions.constants';

import { truncateNumber } from 'utils/truncateNumber';

import { CustomerBalanceUpdatedDto } from 'blocks/SocketProvider/types';

import {
  StateWallets,
  WalletAccountV2DtoExtended,
  WalletCurrencyV2DtoExtended,
  WalletTransactionsFiltersType,
} from './wallet.initialState';

/**
 * The `mapCurrencies` function merges two arrays of CurrencyDto objects based on their code property
 * into a single array.
 * @param stateCurrencies - The `stateCurrencies` parameter is an array of `CurrencyDto` objects
 * representing the currencies currently stored in the state. Each `CurrencyDto` object contains
 * information about a specific currency, such as its code, name, and other relevant data.
 * @param payload - The `payload` parameter in the `mapCurrencies` function represents an array of
 * `CurrencyDto` objects that you want to map. These objects likely contain information about different
 * currencies, such as code, name, symbol, etc. The function takes this array of `CurrencyDto` objects
 * and combines
 * @returns The `mapCurrencies` function returns an array of `CurrencyDto` objects that are a
 * combination of the currencies from the `stateCurrencies` array and the `payload` array. The function
 * first creates hash maps for the currencies in the `stateCurrencies` and `payload` arrays based on
 * the currency code. Then it merges these hash maps and returns an array of values from the merged
 */
export const mapCurrencies = (stateCurrencies: Array<CurrencyDto>, payload: Array<CurrencyDto>): CurrencyDto[] => {
  const stateHashMap = stateCurrencies.reduce<Record<string, CurrencyDto>>((result, currency) => {
    if (!currency.code) return result;

    return { ...result, [currency.code]: currency };
  }, {});

  const payloadHashMap = payload.reduce<Record<string, CurrencyDto>>((result, currency) => {
    if (!currency.code) return result;

    return { ...result, [currency.code]: currency };
  }, {});

  return Object.values({ ...stateHashMap, ...payloadHashMap });
};

const createEmptyWallet = (
  account: WalletCurrencyV2Dto,
  accountType: WalletAccountV2Dto['accountType'] = TradingType.SPOT,
): Required<WalletAccountV2DtoExtended> => ({
  ...account,
  currency: account.currency,
  currencyName: account.currencyName,
  currencyType: account.currencyType ?? WalletType.CRYPTO,
  accountType,
  availableBalance: 0,
  availableBalanceUsd: 0,
  blockedBalance: 0,
  blockedBalanceUsd: 0,
  totalBalance: 0,
  totalBalanceUsd: 0,
  pnl24hPercent: 0,
  pnl24hAbsolute: 0,
  balanceIsUpdated: false,
  decimalPlaces: 0,
  usdRate: 0,
  availableWithdrawalBalance: 0,
  availableWithdrawalBalanceUsd: 0,
  availableAccountTypes: [],
});

export const mapAccountsFromServer = (
  accountsFromServer: WalletCurrencyV2Dto[],
): {
  wallets: Record<string, WalletCurrencyV2DtoExtended>;
  accounts: {
    main: WalletAccountV2DtoExtended[];
    derivatives: WalletAccountV2DtoExtended[];
    earn: WalletAccountV2DtoExtended[];
    stocks: WalletAccountV2DtoExtended[];
  };
} => {
  const sortedByBalanceAccounts = [...accountsFromServer].sort(
    (left, right) => Number(right.totalBalanceUsd) - Number(left.totalBalanceUsd),
  );

  const accounts: Record<'main' | 'derivatives' | 'earn' | 'stocks', WalletAccountV2DtoExtended[]> = {
    main: [],
    derivatives: [],
    earn: [],
    stocks: [],
  };

  const wallets = sortedByBalanceAccounts.reduce<Record<string, WalletCurrencyV2DtoExtended>>((result, account) => {
    const _wallets: WalletAccountV2DtoExtended[] = [];

    const _accounts = account.accounts || [];

    if (!account.currency) return result;

    account.availableAccountTypes?.forEach((availableAccountType) => {
      const walletFromAccount =
        _accounts?.find((x) => x.accountType === availableAccountType) ??
        createEmptyWallet(account, availableAccountType);

      const wallet: WalletAccountV2DtoExtended = {
        ...walletFromAccount,
        totalBalance: truncateNumber(walletFromAccount.totalBalance, account.decimalPlaces),
        totalBalanceUsd: truncateNumber(walletFromAccount.totalBalanceUsd, FIAT_MAX_FRACTION),
        availableBalance: truncateNumber(walletFromAccount.availableBalance, account.decimalPlaces),
        availableWithdrawalBalance: truncateNumber(walletFromAccount.availableWithdrawalBalance, account.decimalPlaces),
        availableBalanceUsd: truncateNumber(walletFromAccount.availableBalanceUsd, FIAT_MAX_FRACTION),
        availableWithdrawalBalanceUsd: truncateNumber(
          walletFromAccount.availableWithdrawalBalanceUsd,
          FIAT_MAX_FRACTION,
        ),
        blockedBalance: truncateNumber(walletFromAccount.blockedBalance, account.decimalPlaces),
        blockedBalanceUsd: truncateNumber(walletFromAccount.blockedBalanceUsd, FIAT_MAX_FRACTION),
        decimalPlaces: account.decimalPlaces,
        availableAccountTypes: account.availableAccountTypes,
        balanceIsUpdated: false,
      };

      switch (wallet.accountType) {
        case TradingType.SPOT:
          accounts.main?.push(wallet);
          break;
        case TradingType.DERIVATIVES:
          accounts.derivatives?.push(wallet);
          break;
        case TradingType.EARN:
          accounts.earn?.push(wallet);
          break;
        case TradingType.STOCK:
          accounts.stocks?.push(wallet);
          break;

        default:
          break;
      }

      _wallets.push(wallet);
    }, {});

    return {
      ...result,
      [account.currency]: {
        ...account,
        totalBalance: truncateNumber(account.totalBalance, account.decimalPlaces),
        totalBalanceUsd: truncateNumber(account.totalBalanceUsd, FIAT_MAX_FRACTION),
        availableBalance: truncateNumber(account.availableBalance, account.decimalPlaces),
        availableBalanceUsd: truncateNumber(account.availableBalanceUsd, FIAT_MAX_FRACTION),
        availableWithdrawalBalance: truncateNumber(account.availableWithdrawalBalance, account.decimalPlaces),
        availableWithdrawalBalanceUsd: truncateNumber(account.availableWithdrawalBalanceUsd, FIAT_MAX_FRACTION),
        blockedBalance: truncateNumber(account.blockedBalance, account.decimalPlaces),
        blockedBalanceUsd: truncateNumber(account.blockedBalanceUsd, FIAT_MAX_FRACTION),
        accounts: _wallets,
      },
    };
  }, {});

  return { wallets, accounts };
};

/**
 * The function `mapWalletsUpdateFromSocket` updates state wallets based on incoming socket data for
 * customer balance changes.
 * @param {StateWallets} stateWallets - `stateWallets` is an object that contains information about the
 * wallets of a customer. Each wallet is identified by a currency code and contains an array of
 * accounts associated with that currency.
 * @param {CustomerBalanceUpdatedDto[]} payload - The `payload` parameter in the
 * `mapWalletsUpdateFromSocket` function is an array of `CustomerBalanceUpdatedDto` objects. Each
 * object in the array represents an update to the customer's balance for a specific currency. The
 * function iterates over each update in the payload array and updates the
 * @returns The function `mapWalletsUpdateFromSocket` returns an updated `StateWallets` object after
 * processing the `payload` of `CustomerBalanceUpdatedDto` objects. It updates the total balance and
 * total balance in USD for each account in the wallets based on the information provided in the
 * payload. The function then returns the updated `StateWallets` object with the changes applied.
 */
export const mapWalletsUpdateFromSocket = (
  stateWallets: StateWallets,
  payload: CustomerBalanceUpdatedDto[],
): StateWallets => {
  const updatedWallets: StateWallets = {};

  payload.forEach((update) => {
    const wallet = stateWallets[update.currency];
    if (!wallet || !wallet.accounts) return;

    const updatedAccounts = wallet.accounts.map((account) => {
      if (account.accountType !== update.tradingType) return account;

      const totalBalanceChange = (update.totalBalance ?? 0) - (account.totalBalance ?? 0);
      const totalBalanceUsdChange = (update.totalBalanceUsd ?? 0) - (account.totalBalanceUsd ?? 0);

      if (wallet.totalBalance !== undefined) wallet.totalBalance += totalBalanceChange;
      if (wallet.totalBalanceUsd !== undefined) wallet.totalBalanceUsd += totalBalanceUsdChange;

      return {
        ...account,
        totalBalance: truncateNumber(update.totalBalance, wallet.decimalPlaces),
        totalBalanceUsd: truncateNumber(update.totalBalanceUsd, FIAT_MAX_FRACTION),
        availableBalance: truncateNumber(update.availableBalance, wallet.decimalPlaces),
        availableBalanceUsd: truncateNumber(update.availableBalanceUsd, FIAT_MAX_FRACTION),
      };
    });

    updatedWallets[update.currency] = {
      ...wallet,
      accounts: updatedAccounts,
    };
  });

  return {
    ...stateWallets,
    ...updatedWallets,
  };
};

/**
 * The function filters customer transactions based on specified types and account trading type.
 * @param {CustomerTransactionV2Dto[]} transactions - The `transactions` parameter is an array of
 * `CustomerTransactionV2Dto` objects, representing wallet transactions for a customer.
 * @param {WalletTransactionsFiltersType} filters - The `filters` parameter in the
 * `mapFiltersForWalletTransactions` function is of type `WalletTransactionsFiltersType`. It is used to
 * filter the `transactions` array based on certain criteria specified in the `filters` object. The
 * function checks if the `types` property is present in the `
 * @returns The function `mapFiltersForWalletTransactions` is returning a filtered array of
 * `CustomerTransactionV2Dto` objects based on the provided `filters` object. The function checks if
 * the `filters.types` array exists, and if it does, it filters the transactions based on the
 * `CustomerTransactionType.TRANSFER` type and the `accountTradingType` specified in the filters.`
 */
export function mapFiltersForWalletTransactions(
  transactions: CustomerTransactionV2Dto[],
  filters: WalletTransactionsFiltersType,
) {
  return transactions.filter((transaction) => {
    if (!filters?.types || !filters?.accountTradingType) return transactions;

    if (filters?.types.includes(CustomerTransactionType.TRANSFER))
      return transaction.destinationAccountType === filters?.accountTradingType;

    return transaction;
  });
}

export function mapWalletTransactionsFilters(filters: WalletTransactionsFiltersType): WalletTransactionsFiltersType {
  const _filters = filters;

  const isSpotTransactionSelected =
    _filters?.types?.includes(CustomerTransactionType.DEPOSIT) ||
    _filters?.types?.includes(CustomerTransactionType.WITHDRAWAL) ||
    _filters?.types?.includes(CustomerTransactionType.TRANSFER) ||
    _filters?.types?.includes(CustomerTransactionType.BONUS) ||
    _filters?.types?.includes(CustomerTransactionType.EARN_REDEMPTION) ||
    _filters?.types?.includes(CustomerTransactionType.EARN_TERMINATION) ||
    _filters?.types?.includes(CustomerTransactionType.EARN_REWARD);

  const isFuturesTransactionSelected = _filters?.types?.includes(CustomerTransactionType.TRANSFER);

  const isEarnTransactionSelected =
    _filters?.types?.includes(CustomerTransactionType.EARN_REDEMPTION) ||
    _filters?.types?.includes(CustomerTransactionType.EARN_TERMINATION) ||
    _filters?.types?.includes(CustomerTransactionType.EARN_TOP_UP);

  const isStocksTransactionSelected = _filters?.types?.includes(CustomerTransactionType.TRANSFER);

  const transactionSelectionMap = {
    [TradingType.SPOT]: isSpotTransactionSelected,
    [TradingType.DERIVATIVES]: isFuturesTransactionSelected,
    [TradingType.EARN]: isEarnTransactionSelected,
    [TradingType.STOCK]: isStocksTransactionSelected,
  };

  // If no wallet type is selected, but type includes EARN_TOP_UP - add type TRANSFER
  if (!_filters?.accountTradingType && _filters?.types?.includes(CustomerTransactionType.EARN_TOP_UP)) {
    // _filters.types.push(CustomerTransactionType.TRANSFER);
    _filters.types = [CustomerTransactionType.TRANSFER];
  }

  if (_filters?.accountTradingType) {
    const isTransactionSelected = transactionSelectionMap[_filters?.accountTradingType];

    // If no relevant transaction type is selected, unset _filters?.types
    if (!isTransactionSelected) _filters.types = undefined;
  }

  if (!_filters?.types?.length) _filters.types = undefined;

  return _filters;
}

const WALLET_TRANSACTIONS_FILTERS_SESSION_STORAGE_NAME = 'webtrader:wallet:transactions:filters';

export const saveWalletTransactionsFiltersInSessionStorage = (filters: WalletTransactionsFiltersType) => {
  try {
    sessionStorage.setItem(WALLET_TRANSACTIONS_FILTERS_SESSION_STORAGE_NAME, JSON.stringify(filters));
  } catch (error) {
    console.log(error);
  }
};

export const loadWalletTransactionsFiltersInSessionStorage = () => {
  try {
    const filters = sessionStorage.getItem(WALLET_TRANSACTIONS_FILTERS_SESSION_STORAGE_NAME);
    if (filters) return JSON.parse(filters);
    return {};
  } catch (error) {
    console.log(error);
    return {};
  }
};
